import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import {
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  StickyNote,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { ESTADOS, SECTORES } from "../directory/directoryFilters";
import {
  CLOSING,
  COMMITMENTS,
  logActivity,
  NEXT,
  OUTCOMES,
} from "../myday/logActivity";
import { fmtDuration, stopCallTimer, useCallTimer } from "../calls/callTimer";
import { DealForm, type DealFormTarget } from "../pipeline/DealForm";
import { DeleteDealDialog } from "../pipeline/DeleteDealDialog";
import { LossDialog, type LossTarget } from "../pipeline/LossDialog";
import { money, type DealRow } from "../pipeline/boardData";
import { DealThermo } from "../thermo/thermo";
import { OfferDot, StagePill } from "../ui";
import { offerColor, useOffers, type Offer } from "../useOffers";
import { mainDeal, PromoteMenu } from "./Ficha360";
import { useReadyResearch, type Person } from "../ai/ai";
import { DenueUpdateButton } from "../denue/denue";
import { mxAt, mxDaysFromToday, mxYmd } from "../mxDate";
import {
  contactEmail,
  contactPhone,
  type ContactRow,
  type Ficha,
} from "./useFicha";

export const fieldCls =
  "h-10 w-full rounded-lg border bg-card px-3 text-sm outline-none focus:border-primary";
export const box = "rounded-xl border bg-card p-4";
export const Quote = ({ children }: { children: ReactNode }) => (
  <p className="rounded-lg border-l-4 border-primary bg-secondary/60 px-3 py-2 text-sm whitespace-pre-line">
    {children}
  </p>
);
export const Label = ({ children }: { children: ReactNode }) => (
  <div className="mb-1 text-xs font-bold text-muted-foreground">{children}</div>
);
export const Empty = ({ children }: { children: ReactNode }) => (
  <div className={cn(box, "text-sm text-muted-foreground")}>{children}</div>
);

/* ------------------------------ Actividad ------------------------------ */
const CHANNELS = [
  ["llamada", "Llamada", Phone],
  ["whatsapp", "WhatsApp", MessageCircle],
  ["email", "Correo", Mail],
  ["nota", "Nota", StickyNote],
] as const;
const chIcon = (ch: string | null) => {
  const Icon = CHANNELS.find(([k]) => k === ch)?.[2] ?? StickyNote;
  return <Icon className="size-4" />;
};

export const NotInCrm = ({
  f,
  refetch,
  what,
}: {
  f: Ficha;
  refetch: () => void;
  what: string;
}) => (
  <div className={cn(box, "flex flex-wrap items-center gap-3")}>
    <span className="flex-1 text-sm text-muted-foreground">
      Todavía no está en el CRM. Pásala para {what}.
    </span>
    {f.d && <PromoteMenu f={f} refetch={refetch} />}
  </div>
);

export const ActivityTab = ({
  f,
  refetch,
}: {
  f: Ficha;
  refetch: () => void;
}) => {
  const { data: offers } = useOffers();
  const notify = useNotify();
  const ids = f.contacts.map((c) => c.id);
  const { data: acts } = useQuery({
    queryKey: ["ficha_acts", ids.join(",")],
    enabled: !!ids.length,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("contact_notes")
        .select("id, text, date, channel, outcome, deal_id, duration_seconds")
        .in("contact_id", ids)
        .order("date", { ascending: false })
        .limit(100);
      return data ?? [];
    },
  });
  const main = mainDeal(f, offers);
  const [channel, setChannel] = useState("llamada");
  const [outcome, setOutcome] = useState("");
  const [dealId, setDealId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [next, setNext] = useState(0);
  const [commitment, setCommitment] = useState("");
  const [saving, setSaving] = useState(false);
  const [loss, setLoss] = useState<LossTarget | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const timer = useCallTimer();
  useEffect(() => setDealId(main?.id ?? null), [main?.id]);
  // El cronómetro manda mientras esté activo (Bloque L.2); si se detiene solo, el número
  // ya calculado queda editable para el siguiente Registrar.
  useEffect(() => {
    if (timer.active) setDuration(timer.seconds);
  }, [timer.active, timer.seconds]);

  if (!f.companyId)
    return (
      <NotInCrm f={f} refetch={refetch} what="empezar a registrar actividad" />
    );
  if (!f.contacts.length)
    return (
      <Empty>
        Agrega un contacto (pestaña Contactos) para registrar actividad.
      </Empty>
    );
  const deal = f.deals.find((d) => d.id === dealId);
  const closing = CLOSING.includes(outcome);

  const save = async () => {
    setSaving(true);
    try {
      await logActivity({
        contactId: f.contacts[0].id,
        dealId,
        channel,
        outcome,
        note,
        nextDays: closing ? 0 : next,
        nextTaskType:
          channel === "email"
            ? "email"
            : channel === "whatsapp"
              ? "whatsapp"
              : "call",
        nextTaskText: `Seguimiento: ${note.trim().slice(0, 60) || f.name}`,
        close: "none",
        deal: deal
          ? {
              stage: deal.stage,
              stages: offers?.find((o) => o.id === deal.offer_id)?.stages ?? [],
            }
          : undefined,
        durationSeconds: channel === "llamada" ? duration : null,
        commitment: commitment || null,
      });
      notify("Actividad registrada", { type: "success" });
      setNote("");
      setOutcome("");
      setNext(0);
      setCommitment("");
      setDuration(null);
      refetch();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setSaving(false);
  };
  const submit = () => {
    if (channel === "llamada" && timer.active) stopCallTimer();
    if (channel === "llamada" && duration != null && duration > 90 * 60) {
      if (
        !window.confirm(
          `${fmtDuration(duration)} es una llamada muy larga (posible olvido). ¿Guardar de todas formas?`,
        )
      )
        return;
    }
    // "No interesado": primero el motivo de pérdida (PRD B.5); si cancela, no se guarda nada
    if (outcome === "no_interesado" && deal)
      return setLoss({
        id: deal.id,
        key: deal.key,
        company_name: f.name,
        offer_id: deal.offer_id,
      });
    save();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className={cn(box, "flex flex-col gap-2")}>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Canal">
          {CHANNELS.map(([k, label, Icon]) => (
            <Button
              key={k}
              size="sm"
              variant={channel === k ? "default" : "outline"}
              onClick={() => setChannel(k)}
            >
              <Icon /> {label}
            </Button>
          ))}
        </div>
        {channel === "llamada" && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {timer.active && (
              <span className="font-mono font-semibold">
                {timer.paused ? "Pausado " : ""}
                {fmtDuration(timer.seconds)}
              </span>
            )}
            {timer.active && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => (timer.paused ? timer.resume() : timer.pause())}
              >
                {timer.paused ? "Reanudar" : "Pausar"}
              </Button>
            )}
            <label className="flex items-center gap-1.5 text-muted-foreground">
              Duración (min:seg)
              <Input
                className="w-20"
                inputMode="numeric"
                placeholder="0:00"
                value={duration != null ? fmtDuration(duration) : ""}
                onChange={(e) => {
                  const m = e.target.value.match(/^(\d+):?(\d{0,2})$/);
                  setDuration(m ? Number(m[1]) * 60 + Number(m[2] || 0) : null);
                }}
              />
            </label>
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          <select
            aria-label="Resultado"
            className={fieldCls}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          >
            <option value="">
              {channel === "nota" ? "Solo nota" : "Resultado…"}
            </option>
            {OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Oportunidad"
            className={fieldCls}
            value={dealId ?? ""}
            onChange={(e) => setDealId(Number(e.target.value) || null)}
          >
            <option value="">Sin oportunidad</option>
            {f.deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.key} · {offers?.find((o) => o.id === d.offer_id)?.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Compromiso obtenido"
            className={cn(fieldCls, "sm:col-span-2")}
            value={commitment}
            onChange={(e) => setCommitment(e.target.value)}
          >
            <option value="">Compromiso obtenido (opcional)</option>
            {COMMITMENTS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="¿Qué pasó? Escribe una nota…"
        />
        <div className="flex flex-wrap items-center gap-1.5">
          {!closing && (
            <>
              <span className="text-sm text-muted-foreground">
                Siguiente paso:
              </span>
              {NEXT.map((n) => (
                <Button
                  key={n.days}
                  size="sm"
                  variant={next === n.days ? "default" : "outline"}
                  onClick={() => setNext(n.days)}
                >
                  {n.label}
                </Button>
              ))}
            </>
          )}
          <Button
            className="ml-auto"
            onClick={submit}
            disabled={
              saving ||
              (channel !== "nota" && !outcome) ||
              (channel === "nota" && !note.trim())
            }
          >
            Registrar
          </Button>
        </div>
      </div>
      <div className="flex flex-col">
        {acts?.length ? (
          acts.map((a) => (
            <div key={a.id} className="flex gap-3 border-l-2 py-2 pl-3">
              <span className="text-muted-foreground">{chIcon(a.channel)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  {CHANNELS.find(([k]) => k === a.channel)?.[1] ?? "Nota"}
                  {a.outcome && (
                    <span className="rounded-full border px-2 text-xs">
                      {OUTCOMES.find((o) => o.value === a.outcome)?.label ??
                        a.outcome}
                    </span>
                  )}
                  {a.deal_id && (
                    <span className="font-mono text-xs text-muted-foreground">
                      {f.deals.find((d) => d.id === a.deal_id)?.key}
                    </span>
                  )}
                  {a.duration_seconds != null && (
                    <span className="text-xs text-muted-foreground">
                      {fmtDuration(a.duration_seconds)} min
                    </span>
                  )}
                  <span className="text-xs font-medium text-muted-foreground">
                    {new Date(a.date).toLocaleString("es-MX", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-line">{a.text}</div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">
            Todavía no hay actividad.
          </p>
        )}
      </div>
      <LossDialog
        target={loss}
        onClose={(lost) => {
          setLoss(null);
          if (lost) save();
        }}
      />
    </div>
  );
};

/* -------------------------------- Datos -------------------------------- */
// [campo del directorio, campo de companies, etiqueta, opciones]
const FIELDS: [string, string | null, string, string[]?][] = [
  ["nombre_comercial", "name", "Nombre comercial"],
  ["razon_social", null, "Razón social"],
  ["actividad", "description", "Actividad"],
  ["sector", "sector", "Sector", SECTORES],
  [
    "rango_personal",
    null,
    "Empleados",
    [
      "0 a 5 personas",
      "6 a 10 personas",
      "11 a 30 personas",
      "31 a 50 personas",
      "51 a 100 personas",
      "101 a 250 personas",
      "251 y más personas",
    ],
  ],
  ["telefono", "phone_number", "Teléfono"],
  ["email", null, "Correo"],
  [
    "email_tipo",
    null,
    "Tipo de correo",
    ["corporativo", "personal", "sin_email"],
  ],
  ["sitio_web", "website", "Sitio web"],
  ["direccion", "address", "Dirección"],
  ["municipio", "city", "Municipio"],
  ["cp", "zipcode", "C.P."],
  ["estado", "state_abbr", "Estado", ESTADOS],
];

export const DataTab = ({ f, refetch }: { f: Ficha; refetch: () => void }) => {
  const notify = useNotify();
  const src = (k: string, c: string | null): string =>
    String(
      (f.d
        ? (f.d as Record<string, unknown>)[k]
        : c && f.co
          ? (f.co as Record<string, unknown>)[c]
          : "") ?? "",
    );
  const fields = FIELDS.filter(([, c]) => f.d || c); // sin directorio: solo campos de la empresa
  const initial = Object.fromEntries(fields.map(([k, c]) => [k, src(k, c)]));
  const [v, setV] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(initial), [f.d, f.co]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = fields.filter(([k]) => (v[k] ?? "") !== (initial[k] ?? ""));

  const save = async () => {
    setSaving(true);
    const sb = getSupabaseClient();
    const val = (k: string) => v[k].trim() || null;
    try {
      if (f.d) {
        const { error } = await sb.rpc("directory_update_fields", {
          p_dir: f.d.id,
          p_fields: Object.fromEntries(changed.map(([k]) => [k, val(k)])),
        });
        if (error) throw error;
      }
      // Reflejar en la empresa del CRM los campos que tiene
      const co = changed.filter(([, c]) => c);
      if (f.companyId && co.length) {
        const { error } = await sb
          .from("companies")
          .update(
            Object.fromEntries(
              co.map(([k, c]) => [
                c!,
                k === "nombre_comercial" ? (val(k) ?? f.name) : val(k),
              ]),
            ),
          )
          .eq("id", f.companyId);
        if (error) throw error;
      }
      notify(
        f.d
          ? "Guardado. Estos campos ya no los cambia la sincronización del DENUE."
          : "Guardado",
        { type: "success" },
      );
      refetch();
    } catch (e) {
      notify(`No se pudo guardar: ${(e as Error).message}`, { type: "error" });
    }
    setSaving(false);
  };

  const edited = new Set(f.d?.user_edited_fields ?? []);
  return (
    <div className={cn(box, "flex flex-col gap-3")}>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map(([k, , label, opts]) => (
          <label key={k} className="grid gap-1 text-sm font-semibold">
            <span>
              {label}
              {edited.has(k) && (
                <span className="ml-1.5 text-xs font-medium text-muted-foreground">
                  · editado por ti
                </span>
              )}
            </span>
            {opts ? (
              <select
                className={fieldCls}
                value={v[k] ?? ""}
                onChange={(e) => setV({ ...v, [k]: e.target.value })}
              >
                <option value="">—</option>
                {opts.map((o) => (
                  <option key={o} value={o}>
                    {o.replace("_", " ")}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                value={v[k] ?? ""}
                onChange={(e) => setV({ ...v, [k]: e.target.value })}
              />
            )}
          </label>
        ))}
      </div>
      {f.d && (
        <dl className="grid gap-x-4 gap-y-1 border-t pt-3 text-sm sm:grid-cols-[140px_1fr]">
          <dt className="text-muted-foreground">Alta en DENUE</dt>
          <dd>
            {f.d.alta_denue ?? "—"}
            {f.d.denue_id && (
              <span className="text-muted-foreground">
                {" "}
                · clave {f.d.denue_id}
              </span>
            )}
            {f.d.denue_status === "cerrada" && (
              <b className="ml-1 text-[var(--critical)]">
                · cerrada según el DENUE
                {f.d.denue_closed_at
                  ? ` (${new Date(f.d.denue_closed_at).toLocaleDateString("es-MX")})`
                  : ""}
              </b>
            )}
          </dd>
          <dt className="text-muted-foreground">Prioridad</dt>
          <dd>
            {f.d.priority} / 100 · nivel {f.d.tier ?? "—"}
          </dd>
          <dt className="text-muted-foreground">SCIAN</dt>
          <dd>{f.d.scian ?? "—"}</dd>
          <dt className="text-muted-foreground">Fuente</dt>
          <dd>{f.d.source}</dd>
        </dl>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {f.d && (
          <span className="mr-auto">
            <DenueUpdateButton dirId={f.d.id} onDone={refetch} />
          </span>
        )}
        {!!changed.length && (
          <span className="text-sm text-muted-foreground">
            {changed.length} cambio(s) sin guardar
          </span>
        )}
        <Button
          variant="outline"
          disabled={!changed.length || saving}
          onClick={() => setV(initial)}
        >
          Descartar
        </Button>
        <Button disabled={!changed.length || saving} onClick={save}>
          Guardar
        </Button>
      </div>
    </div>
  );
};

/* ------------------------------ Contactos ------------------------------ */
const ROLES = ["Dueño", "Compras", "RR.HH.", "Encargado", "Otro"];
const PREF = [
  ["llamada", "Llamada"],
  ["whatsapp", "WhatsApp"],
  ["email", "Correo"],
];

export const ContactsTab = ({
  f,
  refetch,
}: {
  f: Ficha;
  refetch: () => void;
}) => {
  const [edit, setEdit] = useState<ContactRow | "new" | null>(null);
  if (!f.companyId)
    return (
      <div className="flex flex-col gap-2">
        <NotInCrm f={f} refetch={refetch} what="agregar contactos" />
        <SuggestedPeople f={f} refetch={refetch} />
      </div>
    );
  return (
    <div className="flex flex-col gap-2">
      <SuggestedPeople f={f} refetch={refetch} />
      {f.contacts.map((c) => (
        <div
          key={c.id}
          className={cn(box, "flex flex-wrap items-center gap-3 p-3")}
        >
          <div className="min-w-0 flex-1">
            <b>{[c.first_name, c.last_name].filter(Boolean).join(" ")}</b>
            <div className="text-sm text-muted-foreground">
              {[c.title, contactPhone(c), contactEmail(c)]
                .filter(Boolean)
                .join(" · ")}
              {c.preferred_channel &&
                ` · prefiere ${PREF.find(([k]) => k === c.preferred_channel)?.[1]}`}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEdit(c)}>
            <Pencil /> Editar
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        className="self-start"
        onClick={() => setEdit("new")}
      >
        <Plus /> Agregar contacto (dueño, compras, RR.HH.)
      </Button>
      {edit && (
        <ContactDialog
          f={f}
          c={edit === "new" ? null : edit}
          onClose={() => setEdit(null)}
          refetch={refetch}
        />
      )}
    </div>
  );
};

const norm = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Personas clave que encontró la IA (PRD D.4): se agregan con un clic, solo datos de trabajo. */
const SuggestedPeople = ({ f, refetch }: { f: Ficha; refetch: () => void }) => {
  const { data: r } = useReadyResearch(f.d?.id);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [busy, setBusy] = useState<string | null>(null);
  const have = new Set(
    f.contacts.map((c) => norm(`${c.first_name ?? ""} ${c.last_name ?? ""}`)),
  );
  const people = (r?.dossier?.personas ?? []).filter(
    (p) => p.nombre && !have.has(norm(p.nombre)),
  );
  if (!people.length) return null;
  const add = async (p: Person) => {
    setBusy(p.nombre);
    const [first, ...rest] = p.nombre.trim().split(/\s+/);
    const now = new Date().toISOString();
    const { error } = await getSupabaseClient()
      .from("contacts")
      .insert({
        first_name: first,
        last_name: rest.join(" "),
        title: p.puesto || "Otro",
        phone_jsonb: p.telefono ? [{ number: p.telefono, type: "Work" }] : [],
        email_jsonb: p.email ? [{ email: p.email, type: "Work" }] : [],
        linkedin_url:
          p.perfil_url && /linkedin\.com/i.test(p.perfil_url)
            ? p.perfil_url
            : null,
        background: `Sugerido por la IA · fuente: ${p.fuente ?? "—"}${p.fecha ? ` (${p.fecha})` : ""}`,
        company_id: f.companyId,
        sales_id: identity?.id,
        status: "cold",
        first_seen: now,
        last_seen: now,
        tags: [],
      });
    setBusy(null);
    if (error)
      return notify(`No se pudo agregar: ${error.message}`, { type: "error" });
    notify(`${p.nombre} agregado a contactos`, { type: "success" });
    refetch();
  };
  return (
    <div className={cn(box, "flex flex-col gap-2 border-[var(--ai)] p-3")}>
      <b className="text-sm">Personas clave que encontró la IA</b>
      {people.map((p) => (
        <div key={p.nombre} className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1 text-sm">
            <b>{p.nombre}</b>
            <div className="text-muted-foreground">
              {[p.puesto, p.telefono, p.email].filter(Boolean).join(" · ")}
              {p.perfil_url && (
                <>
                  {" · "}
                  <a
                    href={p.perfil_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    perfil público
                  </a>
                </>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!f.companyId || !!busy}
            title={
              f.companyId ? undefined : "Pásala al CRM para agregar contactos"
            }
            onClick={() => add(p)}
          >
            <Plus /> {busy === p.nombre ? "Agregando…" : "Agregar"}
          </Button>
        </div>
      ))}
    </div>
  );
};

const ContactDialog = ({
  f,
  c,
  onClose,
  refetch,
}: {
  f: Ficha;
  c: ContactRow | null;
  onClose: () => void;
  refetch: () => void;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const knownRole = ROLES.includes(c?.title ?? "")
    ? c!.title!
    : c?.title
      ? "Otro"
      : "Dueño";
  const [v, setV] = useState({
    first_name: c?.first_name ?? "",
    last_name: c?.last_name ?? "",
    role: knownRole,
    title: knownRole === "Otro" ? (c?.title ?? "") : "",
    phone: contactPhone(c ?? undefined) ?? "",
    email: contactEmail(c ?? undefined) ?? "",
    pref: c?.preferred_channel ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) =>
    setV({ ...v, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    const row = {
      first_name: v.first_name.trim(),
      last_name: v.last_name.trim(),
      title: v.role === "Otro" ? v.title.trim() || "Otro" : v.role,
      phone_jsonb: v.phone.trim()
        ? [{ number: v.phone.trim(), type: "Work" }]
        : [],
      email_jsonb: v.email.trim()
        ? [{ email: v.email.trim(), type: "Work" }]
        : [],
      preferred_channel: v.pref || null,
    };
    const sb = getSupabaseClient();
    const now = new Date().toISOString();
    const { error } = c
      ? await sb.from("contacts").update(row).eq("id", c.id)
      : await sb.from("contacts").insert({
          ...row,
          company_id: f.companyId,
          sales_id: identity?.id,
          status: "cold",
          first_seen: now,
          last_seen: now,
          tags: [],
        });
    setSaving(false);
    if (error)
      return notify(`No se pudo guardar: ${error.message}`, { type: "error" });
    notify("Contacto guardado", { type: "success" });
    refetch();
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {c ? "Editar contacto" : "Agregar contacto"}
          </DialogTitle>
          <DialogDescription>{f.name}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-sm font-semibold">
            Nombre
            <Input value={v.first_name} onChange={set("first_name")} />
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Apellido
            <Input value={v.last_name} onChange={set("last_name")} />
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Puesto
            <select className={fieldCls} value={v.role} onChange={set("role")}>
              {ROLES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          {v.role === "Otro" ? (
            <label className="grid gap-1 text-sm font-semibold">
              ¿Cuál?
              <Input value={v.title} onChange={set("title")} />
            </label>
          ) : (
            <span className="max-sm:hidden" />
          )}
          <label className="grid gap-1 text-sm font-semibold">
            Celular / WhatsApp
            <Input inputMode="tel" value={v.phone} onChange={set("phone")} />
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Correo
            <Input type="email" value={v.email} onChange={set("email")} />
          </label>
          <label className="grid gap-1 text-sm font-semibold sm:col-span-2">
            Canal preferido
            <select className={fieldCls} value={v.pref} onChange={set("pref")}>
              <option value="">Sin preferencia</option>
              {PREF.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving || !v.first_name.trim()}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------------------- Oportunidades ---------------------------- */
export const DealsTab = ({ f, refetch }: { f: Ficha; refetch: () => void }) => {
  const { data: offers } = useOffers();
  const [form, setForm] = useState<DealFormTarget | null>(null);
  const [loss, setLoss] = useState<LossTarget | null>(null);
  const [del, setDel] = useState<DealRow | null>(null);
  if (!f.companyId)
    return <NotInCrm f={f} refetch={refetch} what="abrir oportunidades" />;
  const open = (d: DealRow) => {
    const o = offers?.find((x) => x.id === d.offer_id);
    return d.stage !== o?.won_stage && d.stage !== o?.lost_stage;
  };
  const fits = f.d?.fits ?? [];
  const others = (offers ?? [])
    .filter((o) => !f.deals.some((d) => d.offer_id === o.id && open(d)))
    .sort(
      (a, b) => Number(fits.includes(b.slug)) - Number(fits.includes(a.slug)),
    );
  const done = () => {
    refetch();
  };
  return (
    <div className="flex flex-col gap-2">
      {f.deals.map((d) => {
        const o = offers?.find((x) => x.id === d.offer_id);
        return (
          <div
            key={d.id}
            className={cn(box, "flex flex-wrap items-center gap-2 p-3")}
          >
            <span className="font-mono text-sm font-bold">{d.key}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
              <OfferDot color={offerColor(o)} />
              {o?.name}
            </span>
            <StagePill stage={d.stage} />
            <span className="text-sm text-muted-foreground">
              {money(d.amount)} · próximo:{" "}
              {d.next_step_at
                ? d.next_step_at.split("-").reverse().join("/")
                : "—"}
            </span>
            <span className="ml-auto flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setForm({ mode: "edit", deal: d })}
              >
                <Pencil /> Editar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                onClick={() => setDel(d)}
              >
                <Trash2 /> Eliminar
              </Button>
            </span>
          </div>
        );
      })}
      {!f.deals.length && (
        <p className="text-sm text-muted-foreground">Sin oportunidades.</p>
      )}
      <div className={cn(box, "flex flex-wrap items-center gap-2 p-3")}>
        <span className="text-sm font-semibold">
          Abrir oportunidad en otro producto
        </span>
        {others.length ? (
          others.map((o) =>
            f.d ? (
              <PromoteOne key={o.id} f={f} o={o} refetch={refetch} />
            ) : (
              <Button
                key={o.id}
                size="sm"
                variant="outline"
                onClick={() =>
                  setForm({
                    mode: "create",
                    offerId: o.id,
                    companyId: f.companyId!,
                  })
                }
              >
                <Plus /> {o.name}
              </Button>
            ),
          )
        ) : (
          <span className="text-sm text-muted-foreground">
            Ya está en todos los productos.
          </span>
        )}
      </div>
      <DealForm
        target={form}
        onClose={() => {
          setForm(null);
          done();
        }}
        onLose={setLoss}
        onDelete={setDel}
      />
      <LossDialog
        target={loss}
        onClose={() => {
          setLoss(null);
          done();
        }}
      />
      <DeleteDealDialog
        deal={del}
        lostStage={offers?.find((o) => o.id === del?.offer_id)?.lost_stage}
        onClose={() => {
          setDel(null);
          done();
        }}
        onLose={(d) =>
          setLoss({
            id: d.id,
            key: d.key,
            company_name: d.company_name,
            offer_id: d.offer_id,
          })
        }
      />
    </div>
  );
};

/** Nueva oportunidad desde el directorio: promote_directory (respeta No contactar). */
const PromoteOne = ({
  f,
  o,
  refetch,
}: {
  f: Ficha;
  o: Offer;
  refetch: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={busy || f.dnc}
      onClick={async () => {
        setBusy(true);
        const { error } = await getSupabaseClient().rpc("promote_directory", {
          p_directory_id: f.d!.id,
          p_offer_slug: o.slug,
        });
        setBusy(false);
        notify(error ? error.message : `Oportunidad de ${o.name} lista`, {
          type: error ? "error" : "success",
        });
        refetch();
      }}
    >
      <Plus /> {o.name}
    </Button>
  );
};

/* -------------------------------- Tareas -------------------------------- */
const TASK_TYPES = [
  ["call", "Llamada"],
  ["whatsapp", "WhatsApp"],
  ["email", "Correo"],
  ["follow-up", "Seguimiento"],
];
type TaskRow = {
  id: number;
  type: string | null;
  text: string | null;
  due_date: string;
  deal_id: number | null;
  contact_id: number;
};

export const TasksTab = ({ f, refetch }: { f: Ficha; refetch: () => void }) => {
  const notify = useNotify();
  const ids = f.contacts.map((c) => c.id);
  const { data: tasks, refetch: reload } = useQuery({
    queryKey: ["ficha_tasks", ids.join(",")],
    enabled: !!ids.length,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("tasks")
        .select("id, type, text, due_date, deal_id, contact_id")
        .in("contact_id", ids)
        .is("done_date", null)
        .order("due_date");
      return (data ?? []) as TaskRow[];
    },
  });
  const [edit, setEdit] = useState<TaskRow | "new" | null>(null);
  const [del, setDel] = useState<TaskRow | null>(null);
  if (!f.companyId)
    return <NotInCrm f={f} refetch={refetch} what="crear tareas" />;
  if (!ids.length)
    return (
      <Empty>Agrega un contacto (pestaña Contactos) para crear tareas.</Empty>
    );
  const after = () => {
    reload();
    refetch();
  };
  const markDone = async (t: TaskRow) => {
    const { error } = await getSupabaseClient()
      .from("tasks")
      .update({ done_date: new Date().toISOString() })
      .eq("id", t.id);
    notify(error ? error.message : "Tarea hecha", {
      type: error ? "error" : "success",
    });
    after();
  };
  return (
    <div className="flex flex-col gap-2">
      {tasks?.map((t) => (
        <div
          key={t.id}
          className={cn(box, "flex flex-wrap items-center gap-2 p-3")}
        >
          <input
            type="checkbox"
            aria-label={`Marcar hecha: ${t.text}`}
            className="size-5 accent-[var(--primary)]"
            onChange={() => markDone(t)}
          />
          <span className="text-muted-foreground">
            {chIcon(t.type === "call" ? "llamada" : t.type)}
          </span>
          <span className="min-w-0 flex-1 text-sm">
            {t.text}
            {t.deal_id && (
              <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                {f.deals.find((d) => d.id === t.deal_id)?.key}
              </span>
            )}
          </span>
          <DealThermo dealId={t.deal_id} />
          <span className="text-sm text-muted-foreground">
            {new Date(t.due_date).toLocaleDateString("es-MX", {
              day: "numeric",
              month: "short",
            })}
          </span>
          <Button size="sm" variant="outline" onClick={() => setEdit(t)}>
            <Pencil /> Editar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive"
            onClick={() => setDel(t)}
          >
            <Trash2 /> Eliminar
          </Button>
        </div>
      ))}
      {!tasks?.length && (
        <p className="text-sm text-muted-foreground">Sin tareas pendientes.</p>
      )}
      <Button
        variant="outline"
        className="self-start"
        onClick={() => setEdit("new")}
      >
        <Plus /> Nueva tarea
      </Button>
      {edit && (
        <TaskDialog
          f={f}
          t={edit === "new" ? null : edit}
          onClose={() => setEdit(null)}
          after={after}
        />
      )}
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Eliminar la tarea?</DialogTitle>
            <DialogDescription>{del?.text}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                const { error } = await getSupabaseClient()
                  .from("tasks")
                  .delete()
                  .eq("id", del!.id);
                notify(error ? error.message : "Tarea eliminada", {
                  type: error ? "error" : "success",
                });
                setDel(null);
                after();
              }}
            >
              Eliminar tarea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TaskDialog = ({
  f,
  t,
  onClose,
  after,
}: {
  f: Ficha;
  t: TaskRow | null;
  onClose: () => void;
  after: () => void;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { data: offers } = useOffers();
  const tomorrow = () => mxDaysFromToday(1);
  const [v, setV] = useState({
    type: t?.type ?? "call",
    text: t?.text ?? "",
    due: t ? mxYmd(new Date(t.due_date)) : tomorrow(),
    deal: (t?.deal_id ?? mainDeal(f, offers)?.id ?? null) as number | null,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const due = mxAt(v.due, "10:00").toISOString(); // a media mañana (México), como las demás
    const row = {
      type: v.type,
      text: v.text.trim(),
      due_date: due,
      deal_id: v.deal,
    };
    const sb = getSupabaseClient();
    const { error } = t
      ? await sb.from("tasks").update(row).eq("id", t.id)
      : await sb.from("tasks").insert({
          ...row,
          contact_id: f.contacts[0].id,
          sales_id: identity?.id,
        });
    setSaving(false);
    if (error)
      return notify(`No se pudo guardar: ${error.message}`, { type: "error" });
    notify("Tarea guardada", { type: "success" });
    after();
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t ? "Editar tarea" : "Nueva tarea"}</DialogTitle>
          <DialogDescription>{f.name}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-sm font-semibold sm:col-span-2">
            Qué hacer
            <Input
              value={v.text}
              onChange={(e) => setV({ ...v, text: e.target.value })}
              placeholder="Llamar para confirmar la demo"
            />
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Tipo
            <select
              className={fieldCls}
              value={v.type}
              onChange={(e) => setV({ ...v, type: e.target.value })}
            >
              {TASK_TYPES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Fecha
            <Input
              type="date"
              value={v.due}
              onChange={(e) => setV({ ...v, due: e.target.value })}
            />
          </label>
          <label className="grid gap-1 text-sm font-semibold sm:col-span-2">
            Oportunidad
            <select
              className={fieldCls}
              value={v.deal ?? ""}
              onChange={(e) =>
                setV({ ...v, deal: Number(e.target.value) || null })
              }
            >
              <option value="">Sin oportunidad</option>
              {f.deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.key} · {offers?.find((o) => o.id === d.offer_id)?.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving || !v.text.trim() || !v.due}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
